import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

import './index.css';
import {
  BenefitDto,
  BenefitPlanDto,
} from 'enrollment/queries/useBenefitPlansQuery/types';
import { ElectRequestDto } from 'enrollment/features/Plans/PlanCard/types';
import { buildElectionParams } from 'enrollment/features/Plans/PlanCard/utils';
import { DeclineReasonsModal } from './components';
import {
  PlanPopup,
  openPlanPopup,
} from 'enrollment/features/Plans/PlanCard/components';
import {
  PopupContentResponseDto,
  PopupContentRequestDto,
  PopupContentDto,
} from 'enrollment/features/ToDoList/types';
import { POPUP_CONTENT_QUERY_KEY } from 'enrollment/features/ToDoList/utils';
import { useConfigurationQuery } from 'theSource/queries';
import { useApiMutation } from 'theSource/utils';
import { ErrorResponseDto } from 'theSource/types';

interface DeclineProps {
  declineText: string;
  declinePlans?: BenefitPlanDto[];
  orgBenefit?: BenefitDto;
  enrollmentContextType?: string;
  handleDeclineBenefit?: (params: ElectRequestDto) => void;
}

export const DeclineCard = ({
  declineText,
  declinePlans,
  orgBenefit,
  enrollmentContextType,
  handleDeclineBenefit,
}: DeclineProps) => {
  const { t } = useTranslation('enrollment.benefits');
  const isDeclineWithReasons = declinePlans && declinePlans.length > 1;
  const [declineModalOpen, setDeclineModalOpen] = useState(false);

  const hasPopupContent = declinePlans?.some(
    (plan) => plan?.has_org_plan_popups
  );
  const popupPlan = declinePlans?.find((plan) => plan?.has_org_plan_popups);

  const [popupContentData, setPopupContentData] = useState<PopupContentDto>();

  const { configuration } = useConfigurationQuery();
  const apiHost = configuration?.apiHost || '';

  const { mutate: getPopupContent } = useApiMutation<
    PopupContentResponseDto,
    ErrorResponseDto,
    PopupContentRequestDto
  >({
    queryKey: POPUP_CONTENT_QUERY_KEY,
    method: 'post',
    endpoint: '/cart/coverages/popups',
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
    onSuccess: (data) => {
      setPopupContentData(data.data);
    },
  });

  useEffect(() => {
    if (hasPopupContent && popupPlan && !popupContentData) {
      getPopupContent({
        org_benefit_id: orgBenefit?.org_benefit_id,
        life_event_completed: false,
        enrollment_context_type: enrollmentContextType || '',
        election: {
          org_plan_id: popupPlan.plan_id,
          coverage_level_id: popupPlan.level_id,
          dependent_ids: [],
          subscriber_amount: popupPlan.subscriber_premium,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPopupContent]);

  const handleDecline = () => {
    if (hasPopupContent && popupPlan) {
      openPlanPopup(popupPlan.plan_id);
    } else {
      declineBenefit();
    }
  };

  const declineBenefit = () => {
    if (handleDeclineBenefit && declinePlans) {
      if (!isDeclineWithReasons) {
        const plan = declinePlans[0];
        handleDeclineBenefit(
          buildElectionParams({
            plan,
            levelId: plan.level_id,
            orgBenefitId: orgBenefit?.org_benefit_id,
            enrollmentContextType,
          })
        );
      } else {
        setDeclineModalOpen(true);
      }
    }
  };

  return (
    <>
      <div className="decline-card" data-testid="decline-card">
        <div className="decline-icon mr-3"></div>
        <div className="decline-card-align">
          <div className="semiBold message">{declineText}</div>
          <div>
            <ps-button
              level="secondary"
              icon="prohibit"
              label={t('decline')}
              data-testid="decline-button"
              id="decline-button"
              {...{ onClick: () => handleDecline() }}
            ></ps-button>
          </div>
        </div>
      </div>
      <DeclineReasonsModal
        id="decline-reasons-modal"
        declinePlans={declinePlans}
        orgBenefitName={orgBenefit?.org_benefit_name}
        orgBenefitId={orgBenefit?.org_benefit_id}
        enrollmentContextType={enrollmentContextType}
        handleDeclineBenefit={handleDeclineBenefit}
        modalOpen={declineModalOpen}
        setModalOpen={setDeclineModalOpen}
      />
      {hasPopupContent && popupPlan && (
        <PlanPopup
          planId={popupPlan.plan_id}
          popupContent={popupContentData}
          apiHost={apiHost}
          onClose={declineBenefit}
        />
      )}
    </>
  );
};
